import imageCompression from 'browser-image-compression'
import * as exifr from 'exifr'
import { Camera, Check, ImagePlus, LoaderCircle, MapPin, Save, Trash2, Upload, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { photoApiUrl, photoAssetUrl, distanceKm } from '../lib/photos'
import { supabase } from '../lib/supabase'

const FULL_OPTIONS = {
  maxSizeMB: 1.05,
  maxWidthOrHeight: 2200,
  useWebWorker: true,
  fileType: 'image/webp',
  initialQuality: 0.84,
}
const THUMB_OPTIONS = {
  maxSizeMB: 0.14,
  maxWidthOrHeight: 480,
  useWebWorker: true,
  fileType: 'image/webp',
  initialQuality: 0.78,
}

function isHeic(file) {
  return /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)
}

async function browserFriendlyFile(file) {
  if (!isHeic(file)) return file
  const { default: heic2any } = await import('heic2any')
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 })
  const blob = Array.isArray(converted) ? converted[0] : converted
  return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' })
}

async function imageDimensions(file) {
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file)
    const dimensions = { width: bitmap.width, height: bitmap.height }
    bitmap.close()
    return dimensions
  }
  return new Promise((resolve, reject) => {
    const image = new Image()
    const url = URL.createObjectURL(file)
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image dimensions.'))
    }
    image.src = url
  })
}

function nearestCandidate(latitude, longitude, summits, routePoints) {
  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) return null
  const candidates = [
    ...summits.map((summit) => ({
      type: 'summit', id: summit.id, tripId: summit.trip_id || '', label: `${summit.peak_name} · ${summit.country_name}`,
      latitude: summit.latitude, longitude: summit.longitude,
    })),
    ...routePoints.map((point) => ({
      type: 'route', id: point.id, tripId: point.trip_id || '', label: point.label,
      latitude: point.latitude, longitude: point.longitude,
    })),
  ]
  return candidates
    .map((candidate) => ({
      ...candidate,
      distance: distanceKm(latitude, longitude, candidate.latitude, candidate.longitude),
    }))
    .sort((a, b) => a.distance - b.distance)[0] || null
}

function getCapturedAt(metadata) {
  const value = metadata?.DateTimeOriginal || metadata?.CreateDate || metadata?.ModifyDate
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

async function prepareFile(file, summits, routePoints) {
  let gps = null
  let metadata = null
  try {
    ;[gps, metadata] = await Promise.all([
      exifr.gps(file).catch(() => null),
      exifr.parse(file).catch(() => null),
    ])
  } catch {
    gps = null
  }

  const friendly = await browserFriendlyFile(file)
  const [fullBlob, thumbnailBlob] = await Promise.all([
    imageCompression(friendly, FULL_OPTIONS),
    imageCompression(friendly, THUMB_OPTIONS),
  ])
  const fullFile = new File([fullBlob], `${crypto.randomUUID()}.webp`, { type: 'image/webp' })
  const thumbnailFile = new File([thumbnailBlob], `${crypto.randomUUID()}-thumb.webp`, { type: 'image/webp' })
  const dimensions = await imageDimensions(fullFile)
  const nearest = nearestCandidate(gps?.latitude, gps?.longitude, summits, routePoints)
  const canAssociate = nearest && nearest.distance <= (nearest.type === 'summit' ? 5 : 10)
  const canSuggestTrip = nearest && nearest.distance <= 100

  return {
    id: crypto.randomUUID(),
    originalFile: file,
    uploadFile: fullFile,
    thumbnailFile,
    previewUrl: URL.createObjectURL(thumbnailFile),
    originalName: file.name,
    latitude: gps?.latitude ?? '',
    longitude: gps?.longitude ?? '',
    capturedAt: getCapturedAt(metadata),
    caption: '',
    tripId: canSuggestTrip ? nearest.tripId : '',
    summitId: canAssociate && nearest.type === 'summit' ? nearest.id : '',
    routePointId: canAssociate && nearest.type === 'route' ? nearest.id : '',
    nearest,
    width: dimensions.width,
    height: dimensions.height,
    status: 'ready',
    error: '',
  }
}

function associationValue(item) {
  if (item.summitId) return `summit:${item.summitId}`
  if (item.routePointId) return `route:${item.routePointId}`
  return ''
}

function PendingPhotoCard({ item, trips, summits, routePoints, onChange, onRemove }) {
  const candidates = useMemo(() => {
    const summitItems = summits
      .filter((summit) => !item.tripId || String(summit.trip_id || '') === String(item.tripId))
      .map((summit) => ({ value: `summit:${summit.id}`, label: `🏔 ${summit.peak_name}` }))
    const routeItems = routePoints
      .filter((point) => !item.tripId || String(point.trip_id || '') === String(item.tripId))
      .map((point) => ({ value: `route:${point.id}`, label: `📍 ${point.label}` }))
    return [...summitItems, ...routeItems]
  }, [item.tripId, summits, routePoints])

  function setAssociation(value) {
    const [type, id] = value.split(':')
    const summit = type === 'summit' ? summits.find((candidate) => String(candidate.id) === String(id)) : null
    const point = type === 'route' ? routePoints.find((candidate) => String(candidate.id) === String(id)) : null
    const place = summit || point
    onChange({
      ...item,
      summitId: summit?.id || '',
      routePointId: point?.id || '',
      tripId: place?.trip_id || item.tripId,
      latitude: item.latitude === '' && place ? place.latitude : item.latitude,
      longitude: item.longitude === '' && place ? place.longitude : item.longitude,
    })
  }

  return (
    <article className={`pending-photo ${item.status === 'uploading' ? 'is-uploading' : ''}`}>
      <img src={item.previewUrl} alt="Upload preview" />
      <div className="pending-photo__body">
        <div className="pending-photo__top">
          <span><strong>{item.originalName}</strong><small>{Math.round(item.uploadFile.size / 1024)} KB optimised</small></span>
          <button type="button" className="icon-button" onClick={() => onRemove(item.id)} aria-label="Remove selected photo"><X size={15} /></button>
        </div>
        <label>Caption<input value={item.caption} onChange={(event) => onChange({ ...item, caption: event.target.value })} placeholder="Optional caption" /></label>
        <div className="form-row">
          <label>Trip<select value={item.tripId} onChange={(event) => onChange({ ...item, tripId: event.target.value, summitId: '', routePointId: '' })}><option value="">Unassigned</option>{trips.map((trip) => <option key={trip.id} value={trip.id}>{trip.name}</option>)}</select></label>
          <label>Nearby place<select value={associationValue(item)} onChange={(event) => setAssociation(event.target.value)}><option value="">Map position only</option>{candidates.map((candidate) => <option key={candidate.value} value={candidate.value}>{candidate.label}</option>)}</select></label>
        </div>
        <div className="form-row">
          <label>Latitude<input type="number" step="any" value={item.latitude} onChange={(event) => onChange({ ...item, latitude: event.target.value })} placeholder="From photo GPS" /></label>
          <label>Longitude<input type="number" step="any" value={item.longitude} onChange={(event) => onChange({ ...item, longitude: event.target.value })} placeholder="From photo GPS" /></label>
        </div>
        <div className="pending-photo__location">
          <MapPin size={13} />
          {item.latitude !== '' && item.longitude !== ''
            ? item.nearest ? `${item.nearest.label} is ${item.nearest.distance.toFixed(1)} km away` : 'GPS location detected'
            : 'No GPS found — choose a saved place or enter coordinates'}
        </div>
        {item.error && <div className="pending-photo__error">{item.error}</div>}
        {item.status === 'uploaded' && <div className="pending-photo__success"><Check size={14} /> Published</div>}
      </div>
    </article>
  )
}

function ExistingPhotoCard({ photo, trips, onSave, onDelete, busy }) {
  const [caption, setCaption] = useState(photo.caption || '')
  const [tripId, setTripId] = useState(photo.trip_id || '')
  useEffect(() => {
    setCaption(photo.caption || '')
    setTripId(photo.trip_id || '')
  }, [photo.caption, photo.trip_id])

  return (
    <article className="existing-photo">
      <img src={photoAssetUrl(photo.thumbnail_r2_key || photo.r2_key)} alt={photo.caption || 'Trip photo'} />
      <div>
        <input value={caption} onChange={(event) => setCaption(event.target.value)} placeholder="Caption" />
        <select value={tripId} onChange={(event) => setTripId(event.target.value)}><option value="">Unassigned</option>{trips.map((trip) => <option key={trip.id} value={trip.id}>{trip.name}</option>)}</select>
        <small>{Number(photo.latitude).toFixed(4)}, {Number(photo.longitude).toFixed(4)}</small>
      </div>
      <div className="existing-photo__actions">
        <button type="button" className="icon-button" onClick={() => onSave(photo, { caption, trip_id: tripId || null })} disabled={busy} title="Save photo details"><Save size={15} /></button>
        <button type="button" className="icon-button icon-button--danger" onClick={() => onDelete(photo)} disabled={busy} title="Delete photo"><Trash2 size={15} /></button>
      </div>
    </article>
  )
}

export default function PhotoManager({ trips, summits, routePoints, photos, photosReady, photosError, session, busy, setBusy, setNotice, refresh }) {
  const [pending, setPending] = useState([])
  const [preparing, setPreparing] = useState(false)
  const [apiState, setApiState] = useState('checking')

  useEffect(() => {
    let cancelled = false
    fetch(photoApiUrl('/api/photos/health'))
      .then((response) => {
        if (!cancelled) setApiState(response.ok ? 'ready' : 'unavailable')
      })
      .catch(() => { if (!cancelled) setApiState('unavailable') })
    return () => { cancelled = true }
  }, [])

  useEffect(() => () => pending.forEach((item) => URL.revokeObjectURL(item.previewUrl)), [])

  async function chooseFiles(event) {
    const files = [...(event.target.files || [])]
    event.target.value = ''
    if (!files.length) return
    setPreparing(true)
    setNotice(`Preparing ${files.length} photo${files.length === 1 ? '' : 's'}…`)
    const newItems = []
    for (const file of files) {
      try {
        newItems.push(await prepareFile(file, summits, routePoints))
      } catch (error) {
        setNotice(`${file.name}: ${error.message || 'Could not prepare this image.'}`)
      }
    }
    setPending((current) => [...current, ...newItems])
    setPreparing(false)
    setNotice(newItems.length ? `${newItems.length} photo${newItems.length === 1 ? '' : 's'} ready to review.` : 'No photos were added.')
  }

  function updatePending(nextItem) {
    setPending((current) => current.map((item) => item.id === nextItem.id ? nextItem : item))
  }

  function removePending(id) {
    setPending((current) => {
      const item = current.find((candidate) => candidate.id === id)
      if (item) URL.revokeObjectURL(item.previewUrl)
      return current.filter((candidate) => candidate.id !== id)
    })
  }

  async function uploadOne(item, order) {
    const latitude = Number(item.latitude)
    const longitude = Number(item.longitude)
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) throw new Error('Add a GPS location or choose a saved place.')
    const formData = new FormData()
    formData.append('image', item.uploadFile, item.uploadFile.name)
    formData.append('thumbnail', item.thumbnailFile, item.thumbnailFile.name)
    formData.append('tripId', item.tripId || 'unassigned')
    const response = await fetch(photoApiUrl('/api/photos/upload'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: formData,
    })
    const result = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(result.error || `Upload failed (${response.status}).`)

    const payload = {
      owner_id: session.user.id,
      trip_id: item.tripId || null,
      summit_id: item.summitId || null,
      route_point_id: item.routePointId || null,
      r2_key: result.r2Key,
      thumbnail_r2_key: result.thumbnailR2Key,
      original_filename: item.originalName,
      mime_type: 'image/webp',
      width: item.width,
      height: item.height,
      file_size: item.uploadFile.size,
      latitude,
      longitude,
      captured_at: item.capturedAt || null,
      caption: item.caption.trim(),
      display_order: order,
    }
    const { error } = await supabase.from('photos').insert(payload)
    if (error) {
      await fetch(photoApiUrl('/api/photos/delete'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ keys: [result.r2Key, result.thumbnailR2Key] }),
      }).catch(() => null)
      throw error
    }
  }

  async function publishAll() {
    if (!pending.length || !session || busy) return
    if (!photosReady) {
      setNotice('Run supabase/photo-map-upgrade.sql before uploading photos.')
      return
    }
    if (apiState !== 'ready') {
      setNotice('The Cloudflare photo API is not available here yet. Complete the R2 setup and deploy with Wrangler.')
      return
    }
    setBusy(true)
    let uploaded = 0
    for (let index = 0; index < pending.length; index += 1) {
      const item = pending[index]
      if (item.status === 'uploaded') continue
      updatePending({ ...item, status: 'uploading', error: '' })
      try {
        await uploadOne(item, photos.length + index)
        updatePending({ ...item, status: 'uploaded', error: '' })
        uploaded += 1
      } catch (error) {
        updatePending({ ...item, status: 'error', error: error.message || 'Upload failed.' })
      }
    }
    setBusy(false)
    setNotice(`${uploaded} photo${uploaded === 1 ? '' : 's'} published.`)
    await refresh()
    if (uploaded === pending.length) {
      pending.forEach((item) => URL.revokeObjectURL(item.previewUrl))
      setPending([])
    }
  }

  async function updatePhoto(photo, changes) {
    setBusy(true)
    const { error } = await supabase.from('photos').update(changes).eq('id', photo.id)
    setBusy(false)
    setNotice(error ? error.message : 'Photo details updated.')
    if (!error) await refresh()
  }

  async function deletePhoto(photo) {
    if (!window.confirm('Delete this photo permanently?')) return
    setBusy(true)
    const { error } = await supabase.from('photos').delete().eq('id', photo.id)
    if (!error) {
      await fetch(photoApiUrl('/api/photos/delete'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ keys: [photo.r2_key, photo.thumbnail_r2_key].filter(Boolean) }),
      }).catch(() => null)
    }
    setBusy(false)
    setNotice(error ? error.message : 'Photo deleted.')
    if (!error) await refresh()
  }

  return (
    <div className="photo-manager">
      {!photosReady && <div className="route-upgrade-card"><strong>Photo table not ready</strong><p>{photosError || 'Run supabase/photo-map-upgrade.sql once.'}</p></div>}
      {apiState === 'unavailable' && <div className="route-upgrade-card"><strong>R2 upload API not connected</strong><p>Uploads work on the hosted Cloudflare site after the Pages Function and R2 binding are deployed. Existing public photos can still display.</p></div>}

      <section className="photo-upload-card">
        <div className="photo-upload-card__copy">
          <div className="form-title"><ImagePlus size={17} /> Add trip photos</div>
          <p>Select a batch. Summit Hub reads GPS and capture time before compressing each image for the website.</p>
        </div>
        <label className="primary-button photo-file-button">
          {preparing ? <LoaderCircle className="spin" size={16} /> : <Camera size={16} />}
          {preparing ? 'Preparing…' : 'Choose photos'}
          <input type="file" accept="image/*,.heic,.heif" multiple onChange={chooseFiles} disabled={preparing || busy} />
        </label>
      </section>

      {pending.length > 0 && (
        <section className="pending-photo-section">
          <div className="photo-section-head"><span><strong>Review before publishing</strong><small>{pending.length} selected</small></span><button type="button" className="primary-button" onClick={publishAll} disabled={busy || preparing}><Upload size={15} /> Publish all</button></div>
          <div className="pending-photo-list">{pending.map((item) => <PendingPhotoCard key={item.id} item={item} trips={trips} summits={summits} routePoints={routePoints} onChange={updatePending} onRemove={removePending} />)}</div>
        </section>
      )}

      <section className="existing-photo-section">
        <div className="photo-section-head"><span><strong>Published photos</strong><small>{photos.length} total</small></span></div>
        {photos.length ? (
          <div className="existing-photo-grid">{photos.map((photo) => <ExistingPhotoCard key={photo.id} photo={photo} trips={trips} onSave={updatePhoto} onDelete={deletePhoto} busy={busy} />)}</div>
        ) : <div className="photo-empty"><ImagePlus size={25} /><span>No photos published yet.</span></div>}
      </section>
    </div>
  )
}
